import { Component, OnInit } from '@angular/core';
import { FormGroup, FormBuilder, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { DebitNoteService } from '../../../services/debit-note.service';
import { BanksService } from '../../../services/banks.service';
import { ProjectService } from '../../../services/project.service';
import { ParticipantService } from '../../../services/participant.service';
import { ThirdPartiesService } from '../../../services/third-parties.service';
import { LoadingService } from '../../../services/loading.service';
import { ToastService } from '../../../services/toast.service';
import { forkJoin } from 'rxjs';
import { BaseFormComponent } from '../../shared/base-form/base-form.component';
import { ConfirmationService } from '../../../services/confirmation.service';

@Component({
    selector: 'app-new-debit-note',
    templateUrl: './new-debit-note.component.html',
    styleUrls: ['./new-debit-note.component.scss']
})
export class NewDebitNoteComponent extends BaseFormComponent implements OnInit {
    debitNoteForm!: FormGroup;
    banks: any[] = [];
    projects: any[] = [];
    participants: any[] = [];
    thirdParties: any[] = [];
    isEditMode: boolean = false;
    noteId: number | null = null;

    constructor(
        private fb: FormBuilder,
        private route: ActivatedRoute,
        router: Router,
        private debitNoteService: DebitNoteService,
        private banksService: BanksService,
        private projectService: ProjectService,
        private participantService: ParticipantService,
        private thirdPartiesService: ThirdPartiesService,
        private loadingService: LoadingService,
        private toastService: ToastService,
        confirmationService: ConfirmationService
    ) {
        super(confirmationService, router);
    }

    // === MÉTODOS REQUERIDOS POR BaseFormComponent ===

    protected override initializeForm(): void {
        this.debitNoteForm = this.fb.group({
            bankId: ['', Validators.required],
            documentNumber: ['', Validators.maxLength(50)],
            documentDate: ['', Validators.required],
            debitNoteType: [0, Validators.required],
            amount: ['', [Validators.required, Validators.min(0.01)]],
            concept: ['', [Validators.required, Validators.maxLength(500)]],
            observations: ['', Validators.maxLength(1000)],
            projectId: [null],
            participantId: [null],
            thirdPartiesId: [null]
        });
    }

    protected override getMainForm(): FormGroup {
        return this.debitNoteForm;
    }

    public override getFormName(): string {
        return 'nota débito';
    }

    public override getCriticalData(): string[] {
        const data: string[] = [];

        const bankId = this.debitNoteForm.get('bankId')?.value;
        const bank = this.banks?.find((b: any) => b.value === parseInt(bankId));
        if (bank) {
            data.push(`Banco: ${bank.label}`);
        }

        const amount = this.debitNoteForm.get('amount')?.value;
        if (amount) {
            data.push(`Monto: ${parseFloat(amount).toLocaleString('es-CO', { style: 'currency', currency: 'COP' })}`);
        }

        const concept = this.debitNoteForm.get('concept')?.value;
        if (concept) {
            data.push(`Concepto: ${concept}`);
        }

        return data;
    }

    protected override async performFormSubmit(): Promise<void> {
        await this.submitDebitNote();
    }

    protected override navigateBack(): void {
        this.router.navigate(['/debit-notes']);
    }

    override ngOnInit(): void {
        super.ngOnInit();
        this.loadData();
        this.checkEditMode();
    }

    private loadData(): void {
        this.loadingService.show();
        forkJoin({
            banks: this.banksService.getAll(),
            projects: this.projectService.getAll(),
            participants: this.participantService.getAll(),
            thirdParties: this.thirdPartiesService.getAll()
        }).subscribe({
            next: (results) => {
                // Map to label/value format for p-dropdown
                // Manejar estructura paginada: data puede ser array directo o {data: [...], totalCount: ...}
                const banksData = Array.isArray(results.banks.data) ? results.banks.data : (results.banks.data?.data || []);
                const projectsData = Array.isArray(results.projects.data) ? results.projects.data : (results.projects.data?.data || []);
                const participantsData = Array.isArray(results.participants.data) ? results.participants.data : (results.participants.data?.data || []);
                const thirdPartiesData = Array.isArray(results.thirdParties.data) ? results.thirdParties.data : (results.thirdParties.data?.data || []);

                this.banks = banksData.map((bank: any) => ({
                    label: `${bank.bankName} - ${bank.accountNumber}`,
                    value: bank.id
                }));
                this.projects = projectsData.map((project: any) => ({
                    label: `${project.projectId} - ${project.name}`,
                    value: project.id
                }));
                this.participants = participantsData.map((p: any) => ({
                    label: `${p.typeDocument} ${p.documentId} - ${p.firstName} ${p.lastName}`,
                    value: p.id
                }));
                this.thirdParties = thirdPartiesData.map((t: any) => ({
                    label: `${t.documentId} - ${t.firstName} ${t.lastName}`,
                    value: t.id
                }));
                this.loadingService.hide();
            },
            error: (err) => {
                console.error('Error loading data:', err);
                this.toastService.showToast('error', 'Error al cargar los datos');
                this.loadingService.hide();
            }
        });
    }

    private checkEditMode(): void {
        this.route.params.subscribe(params => {
            if (params['id']) {
                this.isEditMode = true;
                this.noteId = +params['id'];
                this.loadNoteData(this.noteId);
            }
        });
    }

    // 🔍 Búsqueda server-side para proyectos
    onSearchProjects(event: any): void {
        const query = event.filter;
        if (!query || query.length < 3) {
            this.projectService.getAll().subscribe({
                next: (results) => {
                    const data = Array.isArray(results.data) ? results.data : (results.data?.data || []);
                    this.projects = data.map((p: any) => ({ label: `${p.projectId} - ${p.name}`, value: p.id }));
                },
                error: (err) => console.error('Error loading projects:', err)
            });
            return;
        }
        this.projectService.getAllFiltered(query).subscribe({
            next: (results) => {
                const data = Array.isArray(results.data) ? results.data : (results.data?.data || []);
                this.projects = data.map((p: any) => ({ label: `${p.projectId} - ${p.name}`, value: p.id }));
            },
            error: (err) => console.error('Error searching projects:', err)
        });
    }

    // 🔍 Búsqueda server-side para participantes
    onSearchParticipants(event: any): void {
        const query = event.filter;
        if (!query || query.length < 3) {
            this.participantService.getAll().subscribe({
                next: (results) => {
                    const data = Array.isArray(results.data) ? results.data : (results.data?.data || []);
                    this.participants = data.map((p: any) => ({ label: `${p.typeDocument} ${p.documentId} - ${p.firstName} ${p.lastName}`, value: p.id }));
                },
                error: (err) => console.error('Error loading participants:', err)
            });
            return;
        }
        this.participantService.getAllFiltered(query).subscribe({
            next: (results) => {
                const data = Array.isArray(results.data) ? results.data : (results.data?.data || []);
                this.participants = data.map((p: any) => ({ label: `${p.typeDocument} ${p.documentId} - ${p.firstName} ${p.lastName}`, value: p.id }));
            },
            error: (err) => console.error('Error searching participants:', err)
        });
    }

    // 🔍 Búsqueda server-side para terceros
    onSearchThirdParties(event: any): void {
        const query = event.filter;
        if (!query || query.length < 3) {
            this.thirdPartiesService.getAll().subscribe({
                next: (results) => {
                    const data = Array.isArray(results.data) ? results.data : (results.data?.data || []);
                    this.thirdParties = data.map((t: any) => ({ label: `${t.documentId} - ${t.firstName} ${t.lastName}`, value: t.id }));
                },
                error: (err) => console.error('Error loading third parties:', err)
            });
            return;
        }
        this.thirdPartiesService.getAllFiltered(query).subscribe({
            next: (results) => {
                const data = Array.isArray(results.data) ? results.data : (results.data?.data || []);
                this.thirdParties = data.map((t: any) => ({ label: `${t.documentId} - ${t.firstName} ${t.lastName}`, value: t.id }));
            },
            error: (err) => console.error('Error searching third parties:', err)
        });
    }

    private loadNoteData(id: number): void {
        this.loadingService.show();
        this.debitNoteService.getById(id).subscribe({
            next: (response) => {
                const note = response.data;
                this.debitNoteForm.patchValue({
                    bankId: note.bank?.id,
                    documentDate: note.documentDate?.split('T')[0],
                    amount: note.amount,
                    concept: note.concept,
                    observations: note.observations,
                    projectId: note.project?.id,
                    participantId: note.participant?.id,
                    thirdPartiesId: note.thirdParties?.id
                });
                this.loadingService.hide();
            },
            error: (err) => {
                console.error('Error loading note data', err);
                this.toastService.showToast('error', 'Error al cargar la nota débito');
                this.loadingService.hide();
                this.router.navigate(['/debit-notes']);
            }
        });
    }

    async onSubmit(): Promise<void> {
        if (this.debitNoteForm.invalid) {
            this.debitNoteForm.markAllAsTouched();
            this.toastService.showToast('warning', 'Por favor complete todos los campos requeridos');
            return;
        }
        await this.handleFormSubmit();
    }

    private async submitDebitNote(): Promise<void> {
        const formData = {
            ...this.debitNoteForm.value,
            bankId: parseInt(this.debitNoteForm.value.bankId),
            projectId: this.debitNoteForm.value.projectId ? parseInt(this.debitNoteForm.value.projectId) : null,
            participantId: this.debitNoteForm.value.participantId ? parseInt(this.debitNoteForm.value.participantId) : null,
            thirdPartiesId: this.debitNoteForm.value.thirdPartiesId ? parseInt(this.debitNoteForm.value.thirdPartiesId) : null,
            amount: parseFloat(this.debitNoteForm.value.amount)
        };

        this.loadingService.show();

        this.debitNoteService.create(formData).subscribe({
            next: () => {
                this.loadingService.hide();
                this.toastService.showToast('success', 'Nota débito creada exitosamente');
                this.router.navigate(['/debit-notes']);
            },
            error: (err: any) => {
                console.error('Error saving debit note', err);
                this.loadingService.hide();
                const errorMessage = err?.error?.errorMessage || 'Error al guardar la nota débito';
                this.toastService.showToast('error', errorMessage);
            }
        });
    }

    async onCancel(): Promise<void> {
        await this.handleFormCancel('/debit-notes');
    }

    onConfirmationModalConfirmed(): void {
        this.onModalConfirmed();
    }

    onConfirmationModalCancelled(): void {
        this.onModalCancelled();
    }

    onConfirmationModalClosed(): void {
        this.onModalClosed();
    }
}
